import { CSSProperties, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import AssistantChat from 'components/Assistant/AssistantChat'
import useMaestroChat from 'Hooks/useMaestroChat';
import { makeKey, styx } from 'modules/Utils';
import useScreen from 'Hooks/useScreen';
import DemoControls from 'components/Assistant/DemoControls';
import MeetMaestro from 'components/Assistant/MeetMaestro';
import TagSymphony from 'elements/TagSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import useBrand from 'Hooks/useBrand';

interface AssistantPageProps {
    isPublic?: boolean;
    isChat?: boolean;
}

const AssistantPage = ({ isPublic = false }: AssistantPageProps) => {
    const { chatId: chatIdParam } = useParams<{ chatId?: string }>()
    const history = useHistory()
    const { isAuthenticated, loginWithRedirect } = useAuth0()
    const {
        isChatOpen,
        setIsChatOpen,
        resumeChat,
        isResumingChat,
    } = useMaestroChat()
    const {
        mobileView,
        mStyle,
    } = useScreen()

    const loadChat = async (chatId: string) => {
        if (isResumingChat) return;
        try {
            await resumeChat(chatId);
        } catch (error) {
            console.error('Failed to resume chat from URL:', error);
        }
    };


    // Handle chat resumption when chatId is in URL
    useEffect(() => {
        console.log('🔄 chatIdParam', chatIdParam);
        if (chatIdParam) {
            loadChat(chatIdParam);
        }
    }, []);

    const renderCarousel = () => {
        if (!isPublic || isChatOpen) return null;

        // Carousel images data
        const carouselImages = [
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/6644e2406e5272cdf61b7dca_sza.webp', alt: 'SZA' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/6748060268e9755d6d710465_UNIVERSAL-Carousel-Imag.svg', alt: 'Universal Logo' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/66ba9834588ded2ffe1f546a_Summer.webp', alt: 'Summer Walker' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/681be1a62c2022ec9eb172b2_Podcasters-v2.svg', alt: 'Podcasters' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/66ba9822ca5d1abbb21defa6_Tinashe.webp', alt: 'Tinashe' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/681be1a649366f4c18e7f64a_Newsletters-v2.svg', alt: 'Newsletters' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/66ba984db17296cab91c1f24_Cash%20cobain.webp', alt: 'Cash Cobain' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/681be1a6c3b0474047fc722a_Influencers-v2.svg', alt: 'Influencers' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/6748060206a2a07bd3fc6b8f_WMG-Carousel-Imag.svg', alt: 'Warner Music Group' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/681be1a6e17bb658767ef610_eCommerce-v2.svg', alt: 'eCommerce' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/6644e2409796f8b88e5f0b2a_future.webp', alt: 'Future' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/680f985905abbee26488efb5_bulletpitch-Carousel-Imag.svg', alt: 'Bulletpitch' },
            { src: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/681be1a64209719b535d48f4_Entertainers-v2.svg', alt: 'Entertainers' }
        ];
        return (
            <>
                <div className="poweredBySection" style={mStyle(sty, 'poweredBySection')}>
                    <p style={mStyle(sty, 'poweredByText')}>Our technology has powered campaigns for</p>
                    <div className="logoCarouselContainer" style={mStyle(sty, 'logoCarouselContainer')}>
                        <div className="logoCarouselTrack" style={mStyle(sty, 'logoCarouselTrack')}>
                            {carouselImages.map((image, index) => (
                                <img
                                    key={makeKey('first', index)}
                                    className="logoCarouselImage1"
                                    style={mStyle(sty, 'carouselImage')}
                                    src={image.src}
                                    alt={image.alt}
                                    loading="lazy"
                                />
                            ))}

                            {carouselImages.map((image, index) => (
                                <img
                                    key={makeKey('second', index)}
                                    className="logoCarouselImage2"
                                    style={mStyle(sty, 'carouselImage')}
                                    src={image.src}
                                    alt={image.alt}
                                    loading="lazy"
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* CSS for infinite scroll animation */}
                <style>
                    {`
                @keyframes scrollLeft {
                    0% {
                        transform: translateX(0);
                    }
                    100% {
                        transform: translateX(-50%);
                    }
                }
                
                .logoCarouselTrack {
                    animation: scrollLeft 45s linear infinite;
                    will-change: transform;
                }
                
                .logoCarouselTrack:hover {
                    animation-play-state: paused;
                }
                
                .logoCarouselContainer {
                    mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
                    -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
                }
            `}
                </style>
            </>
        )
    }

    const renderFeatureSquares = () => {
        if (!isPublic || isChatOpen) return null;

        const features = [
            {
                title: 'Automate your ads',
                description: 'AI-powered social ads to help you grow reach, followers, views + sales - whatever you want. An easier way to grow and scale your growth.',
                imageUrl: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/6699b4d04b732665a4444c6d_AutomateYourAds.gif'
            },
            {
                title: 'Own your data',
                description: 'A built-in CRM and data collection tools to help you collect, analyze, and own your data, transforming raw information into meaningful insights.',
                imageUrl: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/6699b4d05692dd965d4f91b4_OwnYourData.gif'
            },
            {
                title: 'Build your site',
                description: 'Promote your latest drops, collect audience data, and track your audience with Symphony\'s customizable website builder',
                imageUrl: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/680faca593252023f96abad3_CopyofEventTourPagesHeader-ezgif.com-optimize%20(1).gif'
            },
            {
                title: 'Track your audience',
                description: 'Track your entire audience across social + content platforms in one place. No more hopping between platforms.',
                imageUrl: 'https://cdn.prod.website-files.com/61fc721130a4089acadae508/6699b4d0687ffb2bd8909a19_TrackYourFanbase.gif'
            }
        ];

        return (
            <div className="featuresSection" style={mStyle(sty, 'featuresSection')}>
                <div className="featuresSectionContainer" style={mStyle(sty, 'featuresSectionContainer')}>
                    <div className="featuresHeader" style={sty.featuresHeader}>
                        <TagSymphony
                            className="featuresTag"
                            color="purple"
                            style={sty.featuresTag}
                        >
                            Features ⚡
                        </TagSymphony>
                        <h1 className="featuresTitle" style={mStyle(sty, 'featuresTitle')}>
                            <span style={sty.titleGradient}>A one-stop shop for all your marketing needs</span>
                        </h1>
                        <p className="featuresSubtitle" style={mStyle(sty, 'featuresSubtitle')}>
                            Symphony brings together all of your digital marketing tools into one place.
                            The last platform you'll ever need.
                        </p>
                    </div>

                    <div className="featuresGrid" style={mStyle(sty, 'featuresGrid')}>
                        {features.map((feature, index) => (
                            <div
                                key={makeKey('feature', index)}
                                className="featureCard"
                                style={mStyle(sty, 'featureCard')}
                            >
                                <div className="featureImageContainer" style={sty.featureImageContainer}>
                                    <img
                                        className="featureImage"
                                        style={sty.featureImage}
                                        src={feature.imageUrl}
                                        alt={feature.title}
                                        loading="lazy"
                                    />
                                </div>
                                <div className="featureContent" style={sty.featureContent}>
                                    <h3 className="featureTitle" style={mStyle(sty, 'featureTitle')}>
                                        {feature.title}
                                    </h3>
                                    <p className="featureDescription" style={mStyle(sty, 'featureDescription')}>
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* <div className="featuresButton" style={sty.featuresButton}>
                        <ButtonSymphony
                            className="getStartedButton"
                            variant="primary"
                            color="purple"
                            size="large"
                            onClick={() => {
                                // Handle getting started action
                                console.log('Get started clicked');
                            }}
                        >
                            Get started for free
                        </ButtonSymphony>
                    </div> */}
                </div>
            </div>
        )
    }

    const renderCtaSection = () => {
        if (!isPublic || isChatOpen) return null;

        const handleGetStarted = () => {
            if (isAuthenticated) {
                // Navigate to Maestro
                history.push('/chats')
            } else {
                // Redirect to login
                loginWithRedirect()
            }
        }

        return (
            <div className="ctaSection" style={mStyle(sty, 'ctaSection')}>
                <div className="ctaSectionContainer" style={mStyle(sty, 'ctaSectionContainer')}>
                    <h2 className="ctaTitle" style={mStyle(sty, 'ctaTitle')}>
                        Automate your growth with your own AI and Symphony's advanced marketing toolset
                    </h2>
                    <p className="ctaSubtitle" style={mStyle(sty, 'ctaSubtitle')}>
                        Join 100K+ creators
                    </p>
                    <ButtonSymphony
                        className="ctaButton"
                        variant="primary"
                        color="purple"
                        size="large"
                        onClick={handleGetStarted}
                    >
                        Get started for free
                    </ButtonSymphony>
                </div>
            </div>
        )
    }

    return (
        <div className="assistantPageWrapper" style={sty.assistantPageWrapper}>
            <div className="assistantPageWrapper_background" style={styx(sty.assistantPageWrapper_background, isPublic && isChatOpen && {
                backgroundImage: 'none',
                height: '100vh'
            })}>
                <AssistantChat />
                {renderCarousel()}
                {renderFeatureSquares()}
                {/* <MeetMaestro isPublic={isPublic} isChatOpen={isChatOpen} styles={sty} /> */}
                {renderCtaSection()}

                {/* Demo Controls - Only show when demo mode is active */}
                <DemoControls />
            </div>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    assistantPageWrapper: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto', // Prevent outer scroll
        backgroundColor: '#181818',
        backgroundImage: 'url(https://cdn.prod.website-files.com/61fc721130a4089acadae508/6685b3d152d239f14cf16fb7_bg.svg)',
        backgroundPosition: '50%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto',
        backgroundAttachment: 'fixed',

        // logged in view - full height
        height: '100%'
    },
    assistantPageWrapper_background: {
        transition: 'background-image 0.3s ease',
        backgroundImage: 'url(https://cdn.prod.website-files.com/61fc721130a4089acadae508/66fe31f8761af995712da428_overlay-header.svg)',
        backgroundPosition: '100% 0',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto',

        // logged in view - full height
        height: '100%'
    },
    poweredBySection: {
        marginTop: 60,
        marginBottom: 40,
    },
    poweredBySection_mobile: {
        marginTop: 30,
        marginBottom: 20,
    },
    poweredByText: {
        color: '#F7F7F2',
        textAlign: 'center',
        fontSize: 18,
        marginBottom: 48,
    },
    logoCarouselContainer: {
        overflow: 'hidden',
        width: '100%',
    },
    logoCarouselTrack: {
        display: 'flex',
        gap: 40,
        alignItems: 'center',
        height: '100%',
        whiteSpace: 'nowrap',
    },
    carouselImage: {
        height: 150,
        width: 'auto',
        maxWidth: 200,
        objectFit: 'contain',
        borderRadius: 12,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        flexShrink: 0,
    },
    // Features section styles
    featuresSection: {
        marginTop: 80,
        marginBottom: 80,
        paddingLeft: 20,
        paddingRight: 20,
    },
    featuresSection_mobile: {
        marginTop: 40,
        marginBottom: 40,
        paddingLeft: 16,
        paddingRight: 16,
    },
    featuresSectionContainer: {
        maxWidth: 1200,
        margin: '0 auto',
    },
    featuresHeader: {
        textAlign: 'center',
        marginBottom: 60,
    },
    featuresTag: {
        marginBottom: 24,
    },
    featuresTitle: {
        fontFamily: 'Din", serif',
        fontOpticalSizing: 'auto',
        fontSize: 64,
        fontStyle: 'italic',
        fontWeight: 400,
        lineHeight: 1.1,
        marginBottom: 24,
        margin: 0,
        background: 'linear-gradient(90deg, #d48afe 20%, #a9acfa 45%, #80ccf6 73%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        display: 'inline-block',
    },
    featuresTitle_mobile: {
        fontSize: 40,
        marginBottom: 16,
    },
    titleGradient: {
        background: 'linear-gradient(90deg, #d48afe 20%, #a9acfa 45%, #80ccf6 73%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        display: 'inline-block',
    },
    featuresSubtitle: {
        fontSize: 20,
        color: 'rgba(255, 255, 255, 0.75)',
        fontWeight: 400,
        lineHeight: '140%',
        maxWidth: 600,
        margin: '0 auto',
    },
    featuresSubtitle_mobile: {
        fontSize: 18,
    },
    featuresGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 24,
        marginBottom: 60,
    },
    featuresGrid_mobile: {
        gridTemplateColumns: '1fr',
        gap: 16,
        marginBottom: 40,
    },
    featureCard: {
        backgroundColor: '#252525',
        borderRadius: 16,
        padding: 24,
        border: '1px solid #333333',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },
    featureCard_mobile: {
        padding: 20,
        borderRadius: 12,
    },
    featureImageContainer: {
        marginBottom: 20,
        borderRadius: 12,
        overflow: 'hidden',
        height: 200,
        backgroundColor: '#1A1A1A',
    },
    featureImage: {
        objectFit: 'cover',
        borderRadius: 16,
        width: '100%',
        height: 300,
    },
    featureContent: {
        textAlign: 'left',
        paddingTop: 8,
    },
    featureTitle: {
        color: '#fff',
        textAlign: 'left',
        marginTop: 0,
        marginBottom: 16,
        paddingTop: 0,
        paddingBottom: 0,
        fontSize: 32,
        fontWeight: 400,
        lineHeight: '130%',
    },
    featureTitle_mobile: {
        fontSize: 24,
        marginBottom: 12,
    },
    featureDescription: {
        color: 'rgba(255, 255, 255, 0.75)',
        fontSize: 20,
        fontWeight: 400,
        lineHeight: '140%',
        margin: 0,
        marginTop: 0,
    },
    featureDescription_mobile: {
        fontSize: 16,
    },
    featuresButton: {
        textAlign: 'center',
    },
    // CTA section styles
    ctaSection: {
        marginTop: 80,
        marginBottom: 80,
        paddingLeft: 20,
        paddingRight: 20,
    },
    ctaSection_mobile: {
        marginTop: 40,
        marginBottom: 40,
        paddingLeft: 16,
        paddingRight: 16,
    },
    ctaSectionContainer: {
        maxWidth: 1200,
        margin: '0 auto',
        backgroundImage: 'url(https://cdn.prod.website-files.com/61fc721130a4089acadae508/6685d88e320acb36d1d2a607_Pattern.svg), url(https://cdn.prod.website-files.com/61fc721130a4089acadae508/6685d6eb64277e9399b61bf4_Type%3DTree%20of%20Life%201.webp)',
        backgroundPosition: '50% 100%, 100% 100%',
        backgroundRepeat: 'repeat, no-repeat',
        backgroundSize: 'auto, cover',
        backgroundAttachment: 'scroll, scroll',
        borderRadius: 40,
        display: 'flex',
        flexFlow: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        padding: '88px 120px',
        gridColumnGap: 32,
        gridRowGap: 32,
        gap: 32,
    },
    ctaSectionContainer_mobile: {
        padding: '60px 24px',
        borderRadius: 24,
        gap: 24,
    },
    ctaTitle: {
        fontFamily: 'Din, sans-serif',
        fontSize: 48,
        fontWeight: 500,
        lineHeight: '130%',
        textAlign: 'center',
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
        color: '#fff',
    },
    ctaTitle_mobile: {
        fontSize: 32,
    },
    ctaSubtitle: {
        fontSize: 24,
        color: 'rgba(255, 255, 255, 0.85)',
        fontWeight: 400,
        lineHeight: '140%',
        margin: 0,
        textAlign: 'center',
    },
    ctaSubtitle_mobile: {
        fontSize: 20,
    },
    // Meet Maestro section styles
    meetMaestroSection: {
        marginTop: 80,
        marginBottom: 80,
        paddingLeft: 20,
        paddingRight: 20,
    },
    meetMaestroSection_mobile: {
        marginTop: 40,
        marginBottom: 40,
        paddingLeft: 16,
        paddingRight: 16,
    },
    meetMaestroContainer: {
        maxWidth: 1200,
        margin: '0 auto',
        textAlign: 'center',
    },
    meetMaestroTitle: {
        fontFamily: '"Crimson Pro", serif',
        fontOpticalSizing: 'auto',
        fontSize: 48,
        fontStyle: 'italic',
        fontWeight: 400,
        lineHeight: 1.1,
        marginBottom: 16,
        color: '#FFFFFF',
    },
    meetMaestroTitle_mobile: {
        fontSize: 32,
    },
    meetMaestroDescription: {
        fontSize: 20,
        color: 'rgba(255, 255, 255, 0.75)',
        fontWeight: 400,
        lineHeight: '140%',
        maxWidth: 600,
        margin: '0 auto 32px auto',
    },
    meetMaestroDescription_mobile: {
        fontSize: 18,
    },
    videoPlayerContainer: {
        maxWidth: 800,
        margin: '0 auto 24px auto',
        borderRadius: 16,
        overflow: 'hidden', // Ensures the border radius is applied to the iframe
    },
    videoPlayerContainer_mobile: {
        maxWidth: '100%',
    },
    videoCaption: {
        fontSize: 18,
        color: 'rgba(255, 255, 255, 0.75)',
        fontWeight: 400,
        lineHeight: '140%',
        maxWidth: 600,
        margin: '0 auto',
    },
    videoCaption_mobile: {
        fontSize: 16,
    },
}

export default AssistantPage 